import Pushy from 'pushy-sdk-web'
import axios from 'axios'
import i18n from '@/i18n'
import { actionHandler as handler } from '@/utils/notification'
import type { Action, ActionType, Message } from '@/typings/notification'
import { isElectron } from '@/utils/common'
import { useBus } from './global'

const usePushwoosh = () => {
  const baseUrl = 'https://api.pushwoosh.com/json/1.3'
  let userId = ''
  let email = ''
  let memberShip = ''
  let messageToken = localStorage.getItem('pushyToken')

  const bus = useBus()

  const addPushyListener = (cb: (data: any) => void) => {
    // Handle push notifications (only when web page is open)
    Pushy.setNotificationListener(function (data) {
      // Print notification payload data
      console.info('Received notification: ', data)

      // Attempt to extract the "message" property from the payload: {title: "Hello World!", "message":"Hello World!"}
      cb(data)
      messageDeliveryEvent()
    })
  }

  const registerPushwoosh = async () => {
    const locale = i18n.global.locale.value
    const deviceData = {
      application: import.meta.env.VITE_PUSHWOOSH_APP_CODE,
      push_token: messageToken,
      hwid: messageToken,
      device_type: 11, // 11 for Chrome
      language: locale,
      timezone: Math.round(new Date().getTimezoneOffset() / 60) * -1 * 3600,
      userId: userId,
      tags: {
        member_ship: memberShip,
        message_token: messageToken
      }
    }
    const response = await axios.post(`${baseUrl}/registerDevice`, deviceData, {
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Token ${import.meta.env.VITE_PUSHWOOSH_REST_API_KEY}`
      }
    })
    if (response.data && response.data.status_code === 200) {
      console.info('[Pushwoosh] Device registered successfully:', response.data)
    } else {
      console.error('[Pushwoosh] Invalid response from device registration:', response.data)
    }

    const emailRegisterData = {
      application: import.meta.env.VITE_PUSHWOOSH_APP_CODE,
      push_token: messageToken,
      hwid: messageToken,
      device_type: 7, // 3 for Android, 7 for MacOS
      language: locale,
      email: email,
      timezone: Math.round(new Date().getTimezoneOffset() / 60) * -1 * 3600,
      userId: userId,
      tags: {
        member_ship: memberShip
      }
    }
    const responseEmail = await axios.post(
      `${baseUrl}/registerDevice`,
      {
        request: emailRegisterData
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${import.meta.env.VITE_PUSHWOOSH_REST_API_KEY}`
        }
      }
    )
    if (responseEmail.data && responseEmail.data.status_code === 200) {
      console.info('[Pushwoosh] Device registered successfully with email:', responseEmail.data)
    } else {
      console.error(
        '[Pushwoosh] Invalid response from device registration with email:',
        responseEmail.data
      )
    }
  }

  const subscribePushy = () => {
    Pushy.register({ appId: import.meta.env.VITE_PUSHY_APP_ID })
      .then(function (deviceToken) {
        console.info('Pushy device token: ' + deviceToken)
        messageToken = deviceToken

        if (Pushy.isRegistered()) {
          // Subscribe the user to a topic
          Pushy.subscribe('look')
            .then(async function (data) {
              console.info('Subscribe success: ', data)
            })
            .catch(function (err) {
              // Notify user of failure
              console.error('Subscribe failed: ' + err.message)
            })
        }
      })
      .catch(function (err) {
        console.error('Pushy registration failed: ' + err.message)
      })
  }

  const initPushwoosh = async (accountInfo: {
    userId: string
    email: string
    memberShip: string
  }) => {
    if (!messageToken) return

    userId = accountInfo.userId
    email = accountInfo.email
    memberShip = accountInfo.memberShip

    await registerPushwoosh()
  }

  const setTags = async (tags: Record<string, any>) => {
    console.info('setTags', tags)
    try {
      const deviceData = {
        application: import.meta.env.VITE_PUSHWOOSH_APP_CODE,
        push_token: messageToken,
        hwid: messageToken,
        device_type: 11, // 11 for Chrome
        language: i18n.global.locale.value,
        timezone: Math.round(new Date().getTimezoneOffset() / 60) * -1 * 3600,
        userId: userId,
        tags
      }

      console.info('[Pushwoosh] Setting tags:', deviceData)

      const response = await axios.post(
        `${baseUrl}/registerDevice`,
        {
          request: deviceData
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${import.meta.env.VITE_PUSHWOOSH_REST_API_KEY}`
          }
        }
      )

      if (response.data && response.data.status_code === 200) {
        console.info('[Pushwoosh] Tags set successfully:', response.data)
        return true
      } else {
        console.error('[Pushwoosh] Failed to set tags:', response.data)
        return false
      }
    } catch (error: any) {
      console.error('[Pushwoosh] Failed to set tags:', error.message)
      if (error.response) {
        console.error('[Pushwoosh] Error response:', error.response.data)
      }
      return false
    }
  }

  const postEvent = async (event: string, data: any) => {
    try {
      const eventData = {
        application: import.meta.env.VITE_PUSHWOOSH_APP_CODE,
        hwid: messageToken,
        userId: userId,
        event: event,
        attributes: data,
        timestampUTC: Math.floor(Date.now() / 1000),
        timestampCurrent: Math.floor(Date.now() / 1000),
        device_type: 11 // 11 for Chrome
      }

      console.info('[Pushwoosh] Posting event:', eventData)

      const response = await axios.post(
        `${baseUrl}/postEvent`,
        {
          request: eventData
        },
        {
          headers: {
            'Content-Type': 'application/json',
            Authorization: `Token ${import.meta.env.VITE_PUSHWOOSH_REST_API_KEY}`
          }
        }
      )

      if (response.data && response.data.status_code === 200) {
        console.info('[Pushwoosh] Event posted successfully:', response.data)
        return response.data.response
      } else {
        console.error('[Pushwoosh] Failed to post event:', response.data)
        return null
      }
    } catch (error: any) {
      console.error('[Pushwoosh] Failed to post event:', error.message)
      if (error.response) {
        console.error('[Pushwoosh] Error response:', error.response.data)
      }
      return null
    }
  }

  const messageDeliveryEvent = async () => {
    const data = {
      application: import.meta.env.VITE_PUSHWOOSH_APP_CODE,
      hwid: messageToken
    }

    const response = await axios.post(
      `${baseUrl}/messageDeliveryEvent`,
      {
        request: data
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${import.meta.env.VITE_PUSHWOOSH_REST_API_KEY}`
        }
      }
    )

    if (response.data && response.data.status_code === 200) {
      console.info('[Pushwoosh] Message delivery event pushed successfully:', response.data)
    } else {
      console.error('[Pushwoosh] Failed to push message delivery event:', response.data)
    }
  }

  const pushStat = async () => {
    const data = {
      application: import.meta.env.VITE_PUSHWOOSH_APP_CODE,
      hwid: messageToken,
      userId: userId
    }

    const response = await axios.post(
      `${baseUrl}/pushStat`,
      {
        request: data
      },
      {
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Token ${import.meta.env.VITE_PUSHWOOSH_REST_API_KEY}`
        }
      }
    )

    if (response.data && response.data.status_code === 200) {
      console.info('[Pushwoosh] Stat pushed successfully:', response.data)
    } else {
      console.error('[Pushwoosh] Failed to push stat:', response.data)
    }
  }

  const actionHandler = (action: ActionType | Action) => {
    console.info('Action handler: ', action)
    if (typeof action === 'string') {
      const actionFn = handler[action as ActionType]
      if (typeof actionFn === 'function') {
        actionFn(bus)
      }
    } else {
      const actionFn = handler[action.type]
      if (typeof actionFn === 'function') {
        actionFn(bus, action.data)
      }
    }
  }

  const actionParser = (action: ActionType | ActionType[] | Action | Action[]) => {
    if (Array.isArray(action)) {
      action.forEach((action) => {
        actionHandler(action)
      })
    } else {
      actionHandler(action)
    }
  }

  const onNotificationClick = (data: Message) => {
    pushStat()
    if (data.action) {
      console.info('Received notification action: ', data.action)
      actionParser(data.action)
    }
  }

  const notificationActionListener = () => {
    // 只在 electron 中监听通知事件
    if (isElectron()) {
      window.electronAPI?.ipcRenderer.on(
        'notification-action-handler',
        (_event, action: ActionType | ActionType[] | Action | Action[]) => {
          console.info('Received notification action: ', action)
          actionParser(action)
        }
      )
    }
  }

  return {
    initPushwoosh,
    setTags,
    postEvent,
    subscribePushy,
    addPushyListener,
    pushStat,
    messageDeliveryEvent,
    actionHandler,
    onNotificationClick,
    notificationActionListener
  }
}

export { usePushwoosh }
